import { css, html } from 'lit';

/**
 * The shared auth-card sheet.
 *
 * These five pages (login, register, forgot-password, reset-password,
 * two-factor) already shared one stylesheet — it was just lunako.app's violet
 * one, on teknik.sh's front door. They keep sharing a sheet; it's this one now.
 *
 * They are NOT wrapped in <tk-page>: the router renders them bare, and a
 * centred 420px card wants a different shell from a full page. The head
 * treatment still matches app-construction.ts, scaled down.
 */
export const tkAuthCard = css`
  :host {
    display: block;
    width: 100%;
    min-height: 100vh;
    background: var(--tk-bg);
    color: var(--tk-text);
    font-family: var(--font-family);
    box-sizing: border-box;
  }

  main {
    max-width: 960px;
    margin: 0 auto;
    padding: calc(48px + 3rem) 24px 6rem;
    display: flex;
    justify-content: center;
  }

  .card {
    background: var(--tk-panel);
    border: 1px solid var(--tk-border);
    border-radius: var(--tk-r-card);
    padding: 2rem;
    max-width: 420px;
    width: 100%;
    box-sizing: border-box;
  }

  .eyebrow {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    margin-bottom: 1rem;
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--tk-accent);
  }
  .eyebrow::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--tk-border);
  }

  .title {
    font-family: var(--font-display);
    font-size: 1.75rem;
    font-weight: 600;
    letter-spacing: -0.015em;
    color: var(--tk-text);
    margin: 0 0 0.5rem;
  }

  .sub {
    font-size: 0.9375rem;
    color: var(--tk-muted);
    line-height: 1.6;
    margin: 0 0 1.5rem;
  }

  form { display: flex; flex-direction: column; gap: 0.9rem; }

  .field { display: flex; flex-direction: column; }

  .full { width: 100%; }

  .divider {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 1.25rem 0;
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--tk-dim);
  }
  .divider::before, .divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--tk-border);
  }

  /* Discord's blurple stays — it is their brand mark on their own button, and
     swapping it would make the control harder to recognise, not more on-brand.
     Hover shifts with a filter so this file holds exactly one non-token hex. */
  .discord-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
    width: 100%;
    padding: 0.62rem 1.15rem;
    border: 1px solid transparent;
    border-radius: var(--tk-r-control);
    background: #5865F2;
    color: var(--tk-text);
    font-family: var(--font-family);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: filter 0.15s ease;
  }
  .discord-btn:hover { filter: brightness(1.1); }
  .discord-btn svg { width: 18px; height: 18px; flex-shrink: 0; }

  .foot {
    margin-top: 1.25rem;
    font-size: 0.875rem;
    color: var(--tk-dim);
    line-height: 1.7;
  }
  .foot a { color: var(--tk-accent); text-decoration: none; }
  .foot a:hover { text-decoration: underline; }

  .code-input {
    text-align: center;
    letter-spacing: 0.24em;
    font-family: var(--font-mono);
    font-size: 1.2rem;
    padding: 0.7rem;
  }

  @media (max-width: 560px) {
    main { padding: calc(48px + 1.5rem) 18px 4rem; }
    .card { padding: 1.5rem; }
  }
`;

/** Discord's mark, inline — the old pages hotlinked it from a Webflow CDN. */
export const discordMark = html`
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.65 12.65 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.891.077.077 0 0 0-.041.107c.36.698.772 1.363 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.056c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03ZM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418Zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418Z"/>
  </svg>
`;
